import { createRoot } from "react-dom/client";
import App from "./App";
import {
  MantineProvider,
  DEFAULT_THEME,
  MantineThemeOverride,
} from "@mantine/core";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorBoundaryFallback } from "./components/ErrorBoundaryFallback";
import useDatadogRum from "./hooks/useDatadogRum";
import { blurple } from "./theme/colors";
import MoonService from "./services/moonService";
import { AuthProvider } from "./context/AuthContext";
import { shouldRecordErrorReplay } from "./utils/errorReplayUtils";
import {
  initUserActionTracking,
  getLastUserAction,
} from "./utils/userActionTracker";
import {
  initReplayMonitoring,
  markSessionReplay,
} from "./utils/replayMonitoring";

// Initialize user action tracking for error debugging context
initUserActionTracking();

// Initialize replay monitoring for cost tracking
initReplayMonitoring();

// Load error testing utilities in development only
if (import.meta.env.DEV) {
  import("./utils/errorTestingUtils").catch((err) => {
    console.warn("Failed to load error testing utilities:", err);
  });
}

const theme: MantineThemeOverride = {
  ...DEFAULT_THEME,
  colors: {
    ...DEFAULT_THEME.colors,
    blurple,
  },
  primaryColor: "blurple",
};

export const moonClient = new MoonService({
  baseURL: import.meta.env.VITE_MOON_BASE_URL,
  project: import.meta.env.VITE_MOON_PROJECT,
});

// Initialize environment variables and configurations
const environment = import.meta.env.MODE;
const appVersion = __APP_VERSION__;

const container = document.getElementById("root");
const root = createRoot(container!);

const Main = () => {
  // Initialize Datadog RUM
  const datadogRum = useDatadogRum({ appVersion, environment });

  return (
    <MantineProvider theme={theme}>
      <ErrorBoundary
        fallbackRender={ErrorBoundaryFallback}
        onError={(error, errorInfo) => {
          // Get last user action for context
          const lastUserAction = getLastUserAction();
          const userActionKey = `${lastUserAction.type}_${lastUserAction.target}`;

          // Check if we should trigger replay (filtering + deduplication with user action context)
          const { shouldReplay, occurrenceCount } = shouldRecordErrorReplay(
            error,
            error.stack,
            userActionKey
          );

          const isFiltered = !shouldReplay && occurrenceCount === 0;
          const isDuplicate = !shouldReplay && !isFiltered;

          if (shouldReplay) {
            if (datadogRum.getInitConfiguration()) {
              // Start replay (startSessionReplayRecording is idempotent)
              datadogRum.startSessionReplayRecording();
              markSessionReplay({ errored: true }); // Track replay + session error
            }
          }

          // Always log the error to Datadog with user action context + occurrence count
          datadogRum.addError(error, {
            errorInfo,
            source: "react",
            last_user_action: lastUserAction,
            occurrence_count: occurrenceCount,
            is_duplicate: isDuplicate,
            is_filtered: isFiltered,
          });
        }}
      >
        <AuthProvider>
          <App />
        </AuthProvider>
      </ErrorBoundary>
    </MantineProvider>
  );
};

root.render(<Main />);
